// Blueprint-style 16px line icons. Single-weight stroke, monochrome.
// Ported verbatim from design-source/primitives.jsx — only renaming to TS.

import type { CSSProperties } from 'react';

export type IconName =
  | 'discover'
  | 'world'
  | 'pipeline'
  | 'cases'
  | 'wl'
  | 'agents'
  | 'alerts'
  | 'admin'
  | 'caret'
  | 'caret-r'
  | 'x'
  | 'plus'
  | 'check'
  | 'arrow-r'
  | 'arrow-l'
  | 'arrow-u'
  | 'arrow-d'
  | 'menu'
  | 'split'
  | 'expand'
  | 'pin'
  | 'open'
  | 'compare'
  | 'play'
  | 'eye'
  | 'lock'
  | 'doc'
  | 'graph'
  | 'map'
  | 'filter'
  | 'sort'
  | 'tree'
  | 'audit'
  | 'conf'
  | 'link'
  | 'note'
  | 'live'
  | 'search';

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
}

export function Icon({ name, size = 16, color, style }: IconProps) {
  const stroke = color ?? 'currentColor';
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke,
    strokeWidth: 1,
    strokeLinecap: 'square' as const,
    strokeLinejoin: 'miter' as const,
    style,
  };
  switch (name) {
    case 'discover':
      return (
        <svg {...props}>
          <circle cx="6.5" cy="6.5" r="4.5" />
          <path d="M10 10L14 14" />
        </svg>
      );
    case 'search':
      return (
        <svg {...props}>
          <circle cx="6.5" cy="6.5" r="4.5" />
          <path d="M10 10L14 14" />
        </svg>
      );
    case 'world':
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="6.5" />
          <path d="M1.5 8H14.5M8 1.5C10 4 10 12 8 14.5C6 12 6 4 8 1.5Z" />
        </svg>
      );
    case 'pipeline':
      return (
        <svg {...props}>
          <path d="M2 2H6V6H2zM10 5H14V9H10zM2 10H6V14H2z" />
          <path d="M6 4H8V11H6V12" />
        </svg>
      );
    case 'cases':
      return (
        <svg {...props}>
          <rect x="1.5" y="3.5" width="13" height="10" />
          <path d="M5 3.5V1.5H11V3.5" />
        </svg>
      );
    case 'wl':
      return (
        <svg {...props}>
          <path d="M3 2H13V14L8 11L3 14V2Z" />
        </svg>
      );
    case 'agents':
      return (
        <svg {...props}>
          <rect x="2" y="3" width="12" height="9" />
          <circle cx="6" cy="7.5" r="1" />
          <circle cx="10" cy="7.5" r="1" />
          <path d="M8 1V3M5 12V14M11 12V14" />
        </svg>
      );
    case 'alerts':
      return (
        <svg {...props}>
          <path d="M3.5 11V7C3.5 5 5.5 3 8 3C10.5 3 12.5 5 12.5 7V11L13.5 12H2.5L3.5 11Z" />
          <path d="M6.5 12.5C6.5 13.5 7 14 8 14C9 14 9.5 13.5 9.5 12.5" />
        </svg>
      );
    case 'admin':
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="2" />
          <path d="M8 1V3M8 13V15M1 8H3M13 8H15M3 3L4.5 4.5M11.5 11.5L13 13M3 13L4.5 11.5M11.5 4.5L13 3" />
        </svg>
      );
    case 'caret':
      return (
        <svg {...props}>
          <path d="M4 6L8 10L12 6" />
        </svg>
      );
    case 'caret-r':
      return (
        <svg {...props}>
          <path d="M6 4L10 8L6 12" />
        </svg>
      );
    case 'x':
      return (
        <svg {...props}>
          <path d="M3 3L13 13M13 3L3 13" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...props}>
          <path d="M8 2V14M2 8H14" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <path d="M3 8L7 12L13 4" />
        </svg>
      );
    case 'arrow-r':
      return (
        <svg {...props}>
          <path d="M2 8H14M10 4L14 8L10 12" />
        </svg>
      );
    case 'arrow-l':
      return (
        <svg {...props}>
          <path d="M14 8H2M6 4L2 8L6 12" />
        </svg>
      );
    case 'arrow-u':
      return (
        <svg {...props}>
          <path d="M8 14V2M4 6L8 2L12 6" />
        </svg>
      );
    case 'arrow-d':
      return (
        <svg {...props}>
          <path d="M8 2V14M4 10L8 14L12 10" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...props}>
          <path d="M2 4H14M2 8H14M2 12H14" />
        </svg>
      );
    case 'split':
      return (
        <svg {...props}>
          <rect x="1.5" y="1.5" width="13" height="13" />
          <path d="M8 1.5V14.5" />
        </svg>
      );
    case 'expand':
      return (
        <svg {...props}>
          <path d="M2 6V2H6M14 6V2H10M2 10V14H6M14 10V14H10" />
        </svg>
      );
    case 'pin':
      return (
        <svg {...props}>
          <path d="M5 1.5H11L10 6L13 9H3L6 6L5 1.5Z" />
          <path d="M8 9V14" />
        </svg>
      );
    case 'open':
      return (
        <svg {...props}>
          <path d="M9 2H14V7M14 2L8 8M13 9V13.5H2.5V3H7" />
        </svg>
      );
    case 'compare':
      return (
        <svg {...props}>
          <rect x="1.5" y="1.5" width="6" height="13" />
          <rect x="8.5" y="1.5" width="6" height="13" />
        </svg>
      );
    case 'play':
      return (
        <svg {...props}>
          <path d="M4 2L13 8L4 14V2Z" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...props}>
          <path d="M1.5 8C3 4 5 2 8 2C11 2 13 4 14.5 8C13 12 11 14 8 14C5 14 3 12 1.5 8Z" />
          <circle cx="8" cy="8" r="2" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...props}>
          <rect x="3" y="7" width="10" height="7" />
          <path d="M5 7V5C5 3 6 2 8 2C10 2 11 3 11 5V7" />
        </svg>
      );
    case 'doc':
      return (
        <svg {...props}>
          <path d="M3 1.5H10L13 4.5V14.5H3V1.5Z" />
          <path d="M10 1.5V4.5H13" />
          <path d="M5 7H11M5 9H11M5 11H9" />
        </svg>
      );
    case 'graph':
      return (
        <svg {...props}>
          <circle cx="3" cy="3" r="1.5" />
          <circle cx="13" cy="3" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
          <circle cx="13" cy="9" r="1.5" />
          <path d="M4 4L8 12M12 4L9 12M12.5 8L9 12" />
        </svg>
      );
    case 'map':
      return (
        <svg {...props}>
          <path d="M1.5 3L6 1.5L10 3L14.5 1.5V13L10 14.5L6 13L1.5 14.5V3Z" />
          <path d="M6 1.5V13M10 3V14.5" />
        </svg>
      );
    case 'filter':
      return (
        <svg {...props}>
          <path d="M1.5 2.5H14.5L9.5 8V13.5L6.5 12.5V8L1.5 2.5Z" />
        </svg>
      );
    case 'sort':
      return (
        <svg {...props}>
          <path d="M5 2V12M2 9L5 12L8 9M11 14V4M8 7L11 4L14 7" />
        </svg>
      );
    case 'tree':
      return (
        <svg {...props}>
          <circle cx="3" cy="3" r="1" />
          <circle cx="3" cy="13" r="1" />
          <circle cx="13" cy="3" r="1" />
          <circle cx="13" cy="13" r="1" />
          <path d="M3 4V12M13 4V12M4 3H12M4 13H12" />
        </svg>
      );
    case 'audit':
      return (
        <svg {...props}>
          <rect x="2" y="2" width="12" height="12" />
          <path d="M2 5H14M5 7H11M5 9H11M5 11H9" />
        </svg>
      );
    case 'conf':
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="6" />
          <path d="M5 8L7 10L11 6" />
        </svg>
      );
    case 'link':
      return (
        <svg {...props}>
          <path d="M6 5H4C2 5 1 6 1 8C1 10 2 11 4 11H6M10 5H12C14 5 15 6 15 8C15 10 14 11 12 11H10M5 8H11" />
        </svg>
      );
    case 'note':
      return (
        <svg {...props}>
          <path d="M2 2H14V14H6L2 10V2Z" />
          <path d="M5 5H11M5 7H11M5 9H8" />
        </svg>
      );
    case 'live':
      return (
        <svg {...props}>
          <circle cx="8" cy="8" r="3" />
          <circle cx="8" cy="8" r="6" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <rect x="3" y="3" width="10" height="10" />
        </svg>
      );
  }
}
